/**
 * Support Us Page JavaScript
 *
 * This module handles the account page functionality for authenticated Firebase users.
 * It listens for user authentication events from the FirebaseEmailLinkAuth module
 * and updates the UI accordingly.
 *
 * Features:
 * - Display user email and submission credits
 * - Show/hide authenticated UI elements
 * - Handle page loader
 *
 * @requires firebase (global Firebase SDK instance)
 * @requires site (global WordPress site configuration object)
 */

import { initializeFirebase, isEmailSignInLink, createPageLoaderManager, cleanUrlParams, getUrlParams, hidePageLoader } from './utils.js';

(function() {
  'use strict';

  // Initialize Firebase app with configuration from WordPress
  // The site object is localized from WordPress via wp_localize_script
  // Use shared utility function to handle initialization safely
  initializeFirebase('support-us.js');

  const showSignInOnLoad = window.location.search.includes('showSignIn=true');
  const isPaymentSuccess = window.location.search.includes('payment=success');

  // Check if the current URL is a Firebase email sign-in link
  // This detects when user is returning from clicking a sign-in link in their email
  // Use shared utility function
  const isEmailLinkFlow = isEmailSignInLink();

  // Track if we're in an email link sign-in flow
  // This flag persists even if Firebase cleans the URL before events fire
  // Set to true if email link is detected, cleared when userDataLoaded fires
  let isProcessingEmailLink = isEmailLinkFlow;

  /**
   * Page state constants
   * Defines all possible states the support-us page can be in
   */
  const PageState = {
    LOADING: 'loading',
    PAYMENT_SUCCESS: 'paymentSuccess',
    EXISTING_SUPPORTER: 'existingSupporter',
    SUPPORTER_SELECTION: 'supporterSelection',
    SIGN_IN: 'signIn'
  };

  /**
   * Calculate page state based on current conditions
   * This is a pure function that determines which UI state to show
   * @param {Object} options - State calculation inputs
   * @param {boolean} options.hasAuthResolved - Whether Firebase auth state is known
   * @param {boolean} options.isPaymentSuccess - URL has payment=success param
   * @param {boolean} options.showSignInOnLoad - URL has showSignIn=true param
   * @param {boolean} options.isAuthenticated - User is signed in
   * @param {boolean} options.hasActiveSubscription - User has active Stripe subscription
   * @returns {string} The calculated page state constant
   */
  function calculatePageState(options) {
    const { hasAuthResolved, isPaymentSuccess, showSignInOnLoad, isAuthenticated, hasActiveSubscription } = options;

    // Stay in loading state until Firebase auth resolves
    // This prevents showing content before we know the user's auth state
    if (!hasAuthResolved) return PageState.LOADING;

    // Payment success takes priority (show thank you regardless of other state)
    // This handles the redirect back from Stripe Checkout
    if (isPaymentSuccess) return PageState.PAYMENT_SUCCESS;

    // Show sign-in form if explicitly requested via URL parameter
    // This happens when user clicks "change login" or joins while logged out
    if (showSignInOnLoad) return PageState.SIGN_IN;

    // Authenticated user with active subscription sees existing supporter message
    if (isAuthenticated && hasActiveSubscription) return PageState.EXISTING_SUPPORTER;

    // Default: show subscription options for users to select
    return PageState.SUPPORTER_SELECTION;
  }

  // Cache DOM element references for better performance
  // These elements are defined in page-support-us.twig
  // NOTE: These will be set inside DOMContentLoaded to ensure DOM is ready
  let signedInAsContainer;
  let signedInAsEmail;
  let signOutButtonContainer;
  let showlistSupporterSelectionContainer;
  let firebaseAuthContainer;
  let firebaseEmailLinkAuthContainer;
  let thankYouContainer;
  let existingSupporterContainer;

  let paymentSuccessHandled = false;

  /**
   * Helper to update all signed-in email displays
   * Updates all elements with the signed-in email and shows the signed-in container
   * @param {string|null} email - The email address to display, or null to skip
   */
  function updateSignedInDisplay(email) {
    if (!email) return;
    if (signedInAsEmail) {
      signedInAsEmail.forEach(function(el) {
        el.textContent = email;
      });
    }
    if (signedInAsContainer) {
      signedInAsContainer.forEach(function(c) {
        c.classList.remove('d-none');
      });
    }
  }

  // Create page loader manager using shared utility
  // This ensures the loader is only hidden once, preventing race conditions
  const pageLoader = createPageLoaderManager();

  /**
   * Render the page state by showing/hiding appropriate containers
   * This is the single source of truth for which UI container is visible
   * @param {string} state - The page state constant from PageState
   * @param {string|null} userEmail - Optional email address to display in signed-in UI
   */
  function renderPageState(state, userEmail) {
    // Hide all containers first to ensure clean state
    // This prevents multiple containers from being visible at once
    // This is critical to prevent flashing of wrong states during email link sign-in
    const containers = [
      thankYouContainer,
      existingSupporterContainer,
      showlistSupporterSelectionContainer,
      firebaseEmailLinkAuthContainer
    ];
    containers.forEach(function(c) {
      if (c) {
        c.classList.add('d-none');
      }
    });

    // If loading state, don't show any container (keep loader visible)
    // This ensures we don't flash content while waiting for Firebase to process email links
    if (state === PageState.LOADING) {
      return;
    }

    // Show the appropriate container based on state
    switch (state) {
      case PageState.PAYMENT_SUCCESS:
        if (thankYouContainer) {
          thankYouContainer.classList.remove('d-none');
        }
        break;
      case PageState.EXISTING_SUPPORTER:
        if (existingSupporterContainer) {
          existingSupporterContainer.classList.remove('d-none');
        }
        // Show sign out button for existing supporters
        if (signOutButtonContainer) {
          signOutButtonContainer.classList.remove('d-none');
        }
        break;
      case PageState.SIGN_IN:
        if (firebaseEmailLinkAuthContainer) {
          firebaseEmailLinkAuthContainer.classList.remove('d-none');
        }
        break;
      case PageState.SUPPORTER_SELECTION:
      default:
        if (showlistSupporterSelectionContainer) {
          showlistSupporterSelectionContainer.classList.remove('d-none');
        }
        break;
    }

    // Update signed-in email displays if email is provided
    // This keeps the UI consistent across state changes
    updateSignedInDisplay(userEmail || null);
  }

  /**
   * Handle user data loaded event
   * This is emitted by FirebaseEmailLinkAuth when user signs in and data is fetched
   * Fired when user is authenticated AND user data is fetched from API
   * @param {CustomEvent} event - The custom event with user data
   */
  window.addEventListener('firebase:userDataLoaded', function(event) {
    const { user, userData } = event.detail;
    console.log('support-us.js: User data loaded', userData);
    console.log('support-us.js: Subscription status:', userData.stripe_subscription_status);

    // Clear the email link processing flag now that we have user data
    // This allows future userDataCleared events to render normally
    isProcessingEmailLink = false;

    // Check for payment success query parameters
    // This handles the redirect back from Stripe Checkout when payment is successful
    // Use shared utility function to get URL parameters
    var urlParams = getUrlParams();
    var paymentStatus = urlParams.get('payment');
    var sessionId = urlParams.get('session_id');

    if (paymentStatus === 'success' && sessionId && !paymentSuccessHandled) {
      user.getIdToken().then(function(accessToken) {
        handlePaymentSuccess(sessionId, accessToken);
      });
    }

    // Calculate and render the appropriate page state
    // Auth state is now known, so hasAuthResolved is true
    // Important: Check subscription status explicitly and defensively to avoid flashing wrong state
    // Only show existing supporter if subscription status is explicitly 'active'
    // This prevents showing supporterSelection briefly before the correct state
    const hasActiveSubscription = userData && userData.stripe_subscription_status === 'active';
    console.log('support-us.js: Has active subscription:', hasActiveSubscription, 'Status:', userData?.stripe_subscription_status);

    const state = calculatePageState({
      hasAuthResolved: true,  // Auth state is now known
      isPaymentSuccess: isPaymentSuccess,
      showSignInOnLoad: showSignInOnLoad,
      isAuthenticated: true,
      hasActiveSubscription: hasActiveSubscription
    });
    console.log('support-us.js: Calculated state:', state);

    // Render the state - this will hide all containers first, then show the correct one
    // This atomic operation prevents flashing between states
    renderPageState(state, userData.email || user.email);
    pageLoader.hide();
  });

  /**
   * Handle user data cleared event
   * This is emitted by FirebaseEmailLinkAuth when user signs out
   * Fired when no user is found or user signs out
   * IMPORTANT: During email link sign-in flow, this can fire BEFORE userDataLoaded
   * We must check for email link flow and skip rendering to prevent flashing wrong state
   * @param {CustomEvent} event - The custom event
   */
  window.addEventListener('firebase:userDataCleared', function(event) {
    console.log('support-us.js: User data cleared');

    // If we're processing an email link sign-in flow, don't render yet
    // The userDataLoaded event will fire next and render the correct state
    // This prevents flashing supporterSelection before existingSupporter
    // We use isProcessingEmailLink flag instead of isEmailLinkFlow because
    // Firebase may clean the URL before this event fires
    if (isProcessingEmailLink) {
      console.log('support-us.js: Skipping render during email link flow - waiting for userDataLoaded');
      return;
    }

    // Calculate and render the appropriate page state
    // Auth state is now known (user is signed out), so hasAuthResolved is true
    const state = calculatePageState({
      hasAuthResolved: true,  // Auth state is now known (user is signed out)
      isPaymentSuccess: isPaymentSuccess,
      showSignInOnLoad: showSignInOnLoad,
      isAuthenticated: false,
      hasActiveSubscription: false
    });
    renderPageState(state, null);
    pageLoader.hide();
  });

  /**
   * Handle user data error event
   * This is emitted by FirebaseEmailLinkAuth when API fetch fails
   * Fired if API call to fetch user data fails
   * @param {CustomEvent} event - The custom event with error details
   */
  window.addEventListener('firebase:userDataError', function(event) {
    console.error('support-us.js: Error loading user data', event.detail.error);

    // Still render - show supporter selection as fallback
    // This allows users to still interact with the page even if data fetch fails
    renderPageState(PageState.SUPPORTER_SELECTION, null);
    pageLoader.hide();
  });

  // Handle payment success verification
  // This function verifies the Stripe checkout session and refreshes user credits
  // It requires an authenticated user and access token, so it's called from onAuthStateChanged
  // sessionId: The Stripe checkout session ID from the URL query parameter
  // accessToken: The Firebase auth token for API authentication
  function handlePaymentSuccess(sessionId, accessToken) {
    // Prevent multiple executions if auth state changes multiple times
    if (paymentSuccessHandled) {
      return;
    }
    paymentSuccessHandled = true;
  }

  // on document ready plain js
  document.addEventListener('DOMContentLoaded', function() {
    console.log('support-us.js: DOMContentLoaded');

    // Early return if Stripe integration is disabled
    if (!site.acfOptions.enable_stripe_integration) {
      hidePageLoader();
      return;
    }

    // Get reference to the Firebase Email Link Auth container
    // This is where the auth module emits its events
    firebaseAuthContainer = document.getElementById('firebase-email-link-auth');

    if (!firebaseAuthContainer) {
      console.error('support-us.js: Firebase auth container not found');
      // Don't return early - continue with other functionality
    }

    firebaseEmailLinkAuthContainer = document.getElementById('firebase-email-link-auth-container');

    if (!firebaseEmailLinkAuthContainer) {
      console.error('support-us.js: Firebase email link auth container not found');
      // Don't return early - continue with other functionality
    }

    // Cache DOM element references for better performance
    // These elements are defined in page-support-us.twig
    thankYouContainer = document.getElementById('support-us-thank-you-container');
    signedInAsContainer = document.querySelectorAll('.signed-in-as-container');
    signedInAsEmail = document.querySelectorAll('.signed-in-as');
    signOutButtonContainer = document.querySelector('.sign-out-button-container');
    showlistSupporterSelectionContainer = document.getElementById('showlist-supporter-selection-container');
    existingSupporterContainer = document.getElementById('existing-supporter-container');

    // Check if firebaseEmailLinkAuth has already loaded user data
    // This handles the race condition where auth resolved before DOMContentLoaded
    // If auth has already resolved, we can immediately render the correct state
    if (window.firebaseEmailLinkAuth && window.firebaseEmailLinkAuth.hasLoadedUserData) {
      const userData = window.firebaseEmailLinkAuth.currentUserData;
      const user = window.firebaseEmailLinkAuth.currentUser;

      const state = calculatePageState({
        hasAuthResolved: true,
        isPaymentSuccess: isPaymentSuccess,
        showSignInOnLoad: showSignInOnLoad,
        isAuthenticated: !!user,
        hasActiveSubscription: userData?.stripe_subscription_status === 'active'
      });
      renderPageState(state, userData?.email || user?.email);
      pageLoader.hide();
    } else if (isEmailLinkFlow) {
      // User is returning from email sign-in link
      // Stay in loading state until Firebase processes the link and fires events
      // This prevents flashing the wrong state before sign-in completes
      // The firebase:userDataLoaded event will update the state when sign-in completes
      console.log('support-us.js: Email sign-in link detected, waiting for Firebase to process...');
      // Ensure all containers are hidden while processing email link
      // Don't call renderPageState(LOADING) here - containers are already hidden by default
      // Just ensure we don't show anything until Firebase completes
    }
    // Otherwise, stay in loading state - Firebase events will fire and update the state
    // The event handlers above will call renderPageState() when auth resolves
    // Containers start hidden (d-none class), so nothing will flash

    // Handle showSignIn query parameter
    // This shows the sign-in form when user clicks "change login" or joins while logged out
    if (showSignInOnLoad) {
      // Remove the showSignIn query param from the url
      cleanUrlParams(['showSignIn']);
    }

    const changeLoginButtons = document.querySelectorAll('.change-login');
    changeLoginButtons.forEach(function(button) {
      button.addEventListener('click', async function() {
        // show the loading spinner
        pageLoader.show();

        await firebaseEmailLinkAuth.auth.signOut();

        // add the showSignIn query param to the url and update location before reload
        const url = new URL(window.location.href);
        url.searchParams.set('showSignIn', 'true');
        window.location.replace(url.toString());
      });
    });

    const joinButtons = document.querySelectorAll('.join-btn');
    joinButtons.forEach(function(button) {
      button.addEventListener('click', function() {
        // get product id from data attribute
        const productId = button.getAttribute('data-product-id');
        const priceId = button.getAttribute('data-price-id');

        // if user is is signed in...
        if (firebaseEmailLinkAuth && firebaseEmailLinkAuth.currentUser) {
          // Get Firebase auth token for API authentication
          // This token is required by the backend to verify the user's identity
          firebaseEmailLinkAuth.currentUser.getIdToken().then(function(accessToken) {
            // Construct absolute URLs for success and cancel redirects
            // Stripe requires absolute URLs for redirect purposes
            var currentUrl = window.location.origin + window.location.pathname;
            var successUrl = currentUrl + '?payment=success&session_id={CHECKOUT_SESSION_ID}';
            var cancelUrl = currentUrl + '?payment=cancelled&session_id={CHECKOUT_SESSION_ID}';

            // show spinner
            pageLoader.show();

            // Create the checkout session via API using fetch instead of $.ajax
            fetch('/wp-json/showlist/v1/stripe/checkout-session', {
              method: 'POST',
              headers: {
                'Authorization': 'Bearer ' + accessToken,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                price_id: priceId,
                success_url: successUrl,
                cancel_url: cancelUrl,
                additional_metadata: {
                  is_subscription_purchase: true,
                }
              })
            })
            .then(async function(response) {
              if (!response.ok) {
                // Try to extract and handle API error message in JSON response
                let errorMessage = 'An error occurred while creating the checkout session.';
                let errorJSON = null;
                try {
                  errorJSON = await response.json();
                } catch (e) {
                  // ignore parse error, will handle below
                }
                if (errorJSON && errorJSON.message) {
                  errorMessage = errorJSON.message;
                } else if (response.status === 401) {
                  errorMessage = 'Please sign in to become a Showlist Supporter.';
                } else if (response.status === 400) {
                  errorMessage = 'Invalid request. Please check your selection and try again.';
                }
                alert(errorMessage);
                throw new Error(errorMessage);
              }
              return response.json();
            })
            .then(function(responseJSON) {
              // On successful session creation, redirect to Stripe Checkout
              // The response contains the checkout URL from Stripe
              if (responseJSON && responseJSON.url) {
                window.location.href = responseJSON.url;
              } else {
                // hide spinner
                hidePageLoader();
                // If response doesn't have URL, show error
                alert('Error: Invalid response from server. Please try again.');
              }
            })
            .catch(function(error) {
              // hide spinner
              hidePageLoader();
              // Handle fetch or JSON errors, if not already alerted above
              if (error && error.message && !error.message.startsWith('Error:')) {
                alert('Error: ' + error.message);
              }
            });
          }).catch(function(error) {
            // hide spinner on error
            hidePageLoader();

            // Handle Firebase token retrieval errors
            console.error('Error getting Firebase token:', error);
            alert('Error authenticating. Please try signing in again.');
          });

        } else {
          console.log('support-us.js: User is not signed in');
          // User is not signed in - show sign-in form
          // Use renderPageState to ensure clean state transition
          renderPageState(PageState.SIGN_IN, null);

          // add product id to url as a query parameter without reloading the page
          // This preserves the product selection for after sign-in
          if (productId) {
            const url = new URL(window.location.href);
            url.searchParams.set('pid', productId);
            window.history.pushState({}, '', url.toString());
          }
        }
      });
    });

    // Handle payment cancellation
    // Check for query parameters indicating payment cancellation
    // This runs immediately on page load since it doesn't require authentication
    function handlePaymentCancelled() {
      // Use shared utility function to get URL parameters
      var urlParams = getUrlParams();
      var paymentStatus = urlParams.get('payment');

      if (paymentStatus === 'cancelled') {
        // Payment was cancelled
        // Show cancellation message
        alert('Payment was cancelled. No charges were made.');

        // Clean up URL by removing query parameter using shared utility
        cleanUrlParams(['payment']);
      }
      // Note: Payment success handling is done in onAuthStateChanged callback
      // to ensure the user is authenticated before attempting verification
    }

    // Handle payment cancellation
    // Check for query parameters indicating payment cancellation
    // This runs immediately on page load since it doesn't require authentication
    handlePaymentCancelled();

    // Fallback timeout: hide loader after 3s even if auth events never fire
    // This is a safety net in case Firebase events fail to fire (network error, etc.)
    setTimeout(function() {
      pageLoader.hide();
    }, 3000);
  }, { once: true });
})();

